import { format, parse } from "date-fns";
import Routes from "../../api/routes";
import { get } from "../../api";
import { page_limit } from "utils";

const { VEHICLE_FILTER, VEHICLE_FLEXIBLE, COLORS } = Routes;

/*******************
@purpose : get the full vehicle color list (location-agnostic)
@Author : INIC
******************/
export const getColorList = () => () => {
  return get(COLORS, false).then((res) => {
    if (res?.data) {
      return Promise.resolve(res.data);
    }
    return Promise.reject(res);
  });
};

/*******************
@purpose : query string shared by the search-results and "Available if
           flexible" requests - location, filters and the selected dates
@Parameter : {locationId, filters}
******************/
const buildSearchParams = (locationId, filters = {}) => {
  const {
    categorySlugs = [],
    brandSlugs = [],
    colorIds = [],
    seats = [],
    minPrice,
    maxPrice,
    sortBy,
    startDate,
    endDate,
    startTime,
    endTime,
  } = filters;

  const params = new URLSearchParams();
  params.append("location_id", locationId);
  categorySlugs.forEach((slug) => params.append("category_slug[]", slug));
  brandSlugs.forEach((slug) => params.append("brand_slug[]", slug));
  colorIds.forEach((id) => params.append("color_id[]", id));
  seats.forEach((seat) => params.append("seats[]", seat));

  if (minPrice !== undefined && minPrice !== "") {
    params.append("min_price", minPrice);
  }
  if (maxPrice !== undefined && maxPrice !== "") {
    params.append("max_price", maxPrice);
  }
  if (sortBy) {
    params.append("sort_by", sortBy);
  }
  // Excludes vehicles that are booked/blocked for the selected dates -
  // only sent once both ends of the range are picked, same as the
  // date-picker's own "start_date"/"end_date" params.
  if (startDate && endDate) {
    params.append("start_date", format(parse(startDate, "MMM d, yyyy", new Date()), "yyyy-MM-dd"));
    params.append("end_date", format(parse(endDate, "MMM d, yyyy", new Date()), "yyyy-MM-dd"));
    if (startTime) {
      params.append("start_time", startTime);
    }
    if (endTime) {
      params.append("end_time", endTime);
    }
  }

  return params;
};

/*******************
@purpose : get vehicles for a location with no brand/category preselected,
           optionally narrowed by category/brand/color/price/seats/sort
@Parameter : {locationId, filters}
@Author : INIC
******************/
export const getSearchResultsVehicles =
  (locationId, filters = {}) =>
  () => {
    const { page, pageLimit } = filters;
    const params = buildSearchParams(locationId, filters);

    if (page) {
      params.append("page", page);
    }
    params.append("page_limit", pageLimit || page_limit);

    return get(`${VEHICLE_FILTER}?${params.toString()}`, false).then(
      (res) => {
        if (res?.data) {
          return Promise.resolve(res);
        }
        return Promise.reject(res);
      }
    );
  };

/*******************
@purpose : "Available if flexible" - vehicles that aren't free for the
           selected dates but become free when only the pickup moves later
           or only the return moves earlier. Same filters as the main
           results; not paginated, ordered by the smallest move first. Each
           vehicle carries a `flexible_availability` object with the
           adjusted range. Needs both dates and both times.
@Parameter : {locationId, filters}
******************/
export const getFlexibleVehicles =
  (locationId, filters = {}) =>
  () => {
    const { startDate, endDate, startTime, endTime } = filters;
    if (!startDate || !endDate || !startTime || !endTime) {
      return Promise.resolve([]);
    }

    const params = buildSearchParams(locationId, { ...filters, sortBy: undefined });

    return get(`${VEHICLE_FLEXIBLE}?${params.toString()}`, false).then((res) => {
      if (res?.data) {
        return Promise.resolve(res.data);
      }
      return Promise.reject(res);
    });
  };
